import { findLastIndex } from 'lodash';
import type { Group, Poll } from 'schema/poll';
import { getQuestionGroupId, getQuestionSetId, selectGroupFirstQuestion, selectQuestion } from '../../selects';

export interface GroupScreen {
  type: 'group';
  groupId: string;
}

export interface QuestionScreen {
  type: 'question';
  questionId: string;
}

export type Screen = GroupScreen | QuestionScreen;

/**
 * @param poll Poll
 * @returns Questions and groups properly ordered as "screens" - the same way as in UI
 */
export function pollToScreens(poll: Poll): Screen[] {
  const screens: Screen[] = [];
  const seenGroupIds = new Set<Group['id']>();

  for (const question of poll.questions) {
    const groupId = getQuestionGroupId(question, poll.questionDecks);
    const group = groupId ? poll.groups.find(group => group.id === groupId) : null;

    if (!groupId) {
      screens.push({ type: 'question', questionId: question.id });
    } else if (!seenGroupIds.has(groupId)) {
      if (!group) {
        screens.push({ type: 'question', questionId: question.id });
        continue;
      }
      const newGroupPage: GroupScreen = { type: 'group', groupId };
      seenGroupIds.add(groupId);
      screens.push(newGroupPage);
    }
  }
  return screens;
}

export function findLastSetScreenIndex(poll: Poll, screens: Screen[], setId: string) {
  return findLastIndex(screens, screen => {
    const question =
      screen.type === 'question'
        ? selectQuestion(poll, screen.questionId)
        : selectGroupFirstQuestion(poll, screen.groupId);

    if (question && setId) return getQuestionSetId(question, poll) === setId;
    return false;
  });
}

export function findScreenIndexSafe(
  poll: Poll,
  screens: Screen[],
  state: { groupId?: string | null; questionId?: string | null; setId?: string | null },
) {
  const res = screens.findIndex(screen => {
    const question =
      screen.type === 'question'
        ? selectQuestion(poll, screen.questionId)
        : selectGroupFirstQuestion(poll, screen.groupId);

    if (!question) return true;

    if (state.questionId) return question.id === state.questionId;
    if (state.groupId) return getQuestionGroupId(question, poll.questionDecks) === state.groupId;
    if (state.setId) return getQuestionSetId(question, poll) === state.setId;

    return true;
  });

  return res === -1 ? 0 : res;
}

export function findScreen(
  poll: Poll,
  screens: Screen[],
  state: { groupId?: string | null; questionId?: string | null; setId?: string | null },
) {
  const found = screens[findScreenIndexSafe(poll, screens, state)];
  if (!found) throw new Error('Impossible: Failed to find screen!');
  return found;
}
